export interface ClipboardOptions {
  timeout?: number;
  logErrors?: boolean;
}

export interface ClipboardResult {
  success: boolean;
  error?: Error;
  method?: 'modern' | 'legacy';
}

export class ClipboardError extends Error {
  originalError?: Error;
  type: 'timeout' | 'permission' | 'unsupported' | 'invalid-input' | 'unknown';

  constructor(message: string, type: 'timeout' | 'permission' | 'unsupported' | 'invalid-input' | 'unknown' = 'unknown', originalError?: Error) {
    super(message);
    this.name = 'ClipboardError';
    this.type = type;
    this.originalError = originalError;
  }
}

export const isClipboardSupported = (): boolean => {
  // Comprehensive browser support check
  if (!navigator.clipboard) return false;
  if (!window.isSecureContext) return false;

  // Check if writeText method exists
  if (typeof navigator.clipboard.writeText !== 'function') return false;

  // Additional checks for known problematic environments
  try {
    // iOS Safari in private mode throws on clipboard access
    if (navigator.userAgent.includes('Safari') && navigator.userAgent.includes('iPhone')) {
      // Can't reliably detect private mode, so assume clipboard works
    }
    return true;
  } catch {
    return false;
  }
};

export const copyToClipboard = async (text: string, options: ClipboardOptions = {}): Promise<ClipboardResult> => {
  const { timeout = 5000, logErrors = true } = options;

  if (!text || typeof text !== 'string') {
    const error = new ClipboardError('Invalid text provided for clipboard operation', 'invalid-input');
    return { success: false, error };
  }

  // Use AbortController for proper timeout cleanup
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    if (isClipboardSupported()) {
      try {
        await navigator.clipboard.writeText(text);
        clearTimeout(timeoutId);
        return { success: true, method: 'modern' };
      } catch (error) {
        clearTimeout(timeoutId);

        // Determine error type
        const errorMessage = error instanceof Error ? error.message : String(error);
        let errorType: ClipboardError['type'] = 'unknown';

        if (controller.signal.aborted) {
          errorType = 'timeout';
        } else if (errorMessage.includes('permission') || errorMessage.includes('denied')) {
          errorType = 'permission';
        } else if (errorMessage.includes('not supported') || errorMessage.includes('unsupported')) {
          errorType = 'unsupported';
        }

        const clipboardError = new ClipboardError(
          `Modern clipboard API failed: ${errorMessage}`,
          errorType,
          error instanceof Error ? error : new Error(String(error))
        );

        if (logErrors) {
          console.error('Modern clipboard copy failed:', error);
        }

        // Fallback to legacy method
        return await copyToClipboardLegacy(text);
      }
    }

    // No modern clipboard support, use legacy method
    clearTimeout(timeoutId);
    return await copyToClipboardLegacy(text);
  } catch (error) {
    clearTimeout(timeoutId);

    const clipboardError = new ClipboardError(
      'Clipboard operation failed completely',
      'unknown',
      error instanceof Error ? error : new Error(String(error))
    );

    if (logErrors) {
      console.error('Clipboard copy failed:', error);
    }

    return { success: false, error: clipboardError };
  }
};

const copyToClipboardLegacy = async (text: string): Promise<ClipboardResult> => {
  return new Promise((resolve) => {
    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;

      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      textArea.style.width = '1px';
      textArea.style.height = '1px';
      textArea.style.padding = '0';
      textArea.style.border = 'none';
      textArea.style.outline = 'none';
      textArea.style.boxShadow = 'none';
      textArea.style.background = 'transparent';

      document.body.appendChild(textArea);

      textArea.focus();
      textArea.select();
      textArea.setSelectionRange(0, text.length);

      const success = document.execCommand('copy');
      document.body.removeChild(textArea);

      if (success) {
        resolve({ success: true, method: 'legacy' });
      } else {
        resolve({
          success: false,
          error: new ClipboardError('Legacy clipboard operation failed', 'unsupported')
        });
      }
    } catch (error) {
      resolve({
        success: false,
        error: new ClipboardError('Legacy clipboard operation threw error', 'unknown', error instanceof Error ? error : new Error(String(error)))
      });
    }
  });
};

export const copyTextWithFeedback = async (
  text: string,
  onSuccess?: (result: ClipboardResult) => void,
  onError?: (error: ClipboardError) => void,
  options?: ClipboardOptions
): Promise<void> => {
  const result = await copyToClipboard(text, options);

  if (result.success) {
    onSuccess?.(result);
  } else {
    // Ensure error is always a ClipboardError
    const clipboardError =
      result.error instanceof ClipboardError ? result.error : new ClipboardError('Unknown clipboard error', 'unknown', result.error);
    onError?.(clipboardError);
  }
};
